import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import './ConsultingPage.css';

/* ─────────────────────────────────────────────────────────────
   Icons (inline SVG, stroke-based, consistent with PlatformPage)
   ───────────────────────────────────────────────────────────── */
const CheckIcon = (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <polyline points="20 6 9 17 4 12" />
  </svg>
);

const EvaluateIcon = (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <circle cx="11" cy="11" r="7" />
    <path d="M21 21l-4.3-4.3" />
  </svg>
);

const ReceiveIcon = (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M21 16v-2a4 4 0 0 0-4-4H7a4 4 0 0 0-4 4v2" />
    <path d="M12 11V3" />
    <path d="M8 7l4 4 4-4" />
  </svg>
);

/* Service icons (8) */
const StrategyIcon = (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M3 3v18h18" />
    <path d="M7 15l4-4 3 3 5-7" />
  </svg>
);

const ArchitectureIcon = (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <rect x="3" y="3" width="7" height="7" rx="1" />
    <rect x="14" y="3" width="7" height="7" rx="1" />
    <rect x="3" y="14" width="7" height="7" rx="1" />
    <rect x="14" y="14" width="7" height="7" rx="1" />
    <path d="M10 6.5h4" />
    <path d="M10 17.5h4" />
    <path d="M6.5 10v4" />
    <path d="M17.5 10v4" />
  </svg>
);

const AgentIcon = (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <rect x="4" y="7" width="16" height="12" rx="2" />
    <circle cx="9" cy="13" r="1.2" />
    <circle cx="15" cy="13" r="1.2" />
    <path d="M12 3v4" />
    <path d="M8 19v2" />
    <path d="M16 19v2" />
  </svg>
);

const RagIcon = (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <ellipse cx="12" cy="5" rx="8" ry="3" />
    <path d="M4 5v6c0 1.7 3.6 3 8 3s8-1.3 8-3V5" />
    <path d="M4 11v6c0 1.7 3.6 3 8 3s8-1.3 8-3v-6" />
  </svg>
);

const WorkflowIcon = (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <rect x="3" y="4" width="6" height="6" rx="1" />
    <rect x="15" y="4" width="6" height="6" rx="1" />
    <rect x="9" y="14" width="6" height="6" rx="1" />
    <path d="M9 7h6" />
    <path d="M6 10v3a1 1 0 0 0 1 1h2" />
    <path d="M18 10v3a1 1 0 0 1-1 1h-2" />
  </svg>
);

const GovernanceIcon = (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M12 2l8 4v6c0 5-3.5 8.5-8 10-4.5-1.5-8-5-8-10V6l8-4z" />
    <path d="M9 12l2 2 4-4" />
  </svg>
);

const PromptIcon = (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M4 5h16v11H7l-3 3z" />
    <path d="M8 10h8" />
    <path d="M8 13h5" />
  </svg>
);

const CoachIcon = (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <circle cx="9" cy="8" r="3" />
    <path d="M3 21v-1a6 6 0 0 1 12 0v1" />
    <circle cx="17" cy="6" r="2" />
    <path d="M21 14a4 4 0 0 0-6.5-3.1" />
  </svg>
);

/* ─────────────────────────────────────────────────────────────
   Data
   ───────────────────────────────────────────────────────────── */
const evaluateItems = [
  'Current AI initiatives',
  'AI agent opportunities',
  'Workflow automation potential',
  'Platform and data readiness',
  'Architecture and tooling choices',
  'Team capability gaps',
  'Governance and risk needs',
  '90-day execution priorities'
];

const receiveItems = [
  'AI opportunity map',
  'Use case prioritization',
  'Architecture recommendations',
  'Agent and workflow roadmap',
  'Governance considerations',
  '90-day execution plan'
];

const services = [
  {
    icon: StrategyIcon,
    title: 'AI Strategy & Roadmap',
    desc: 'Define a focused AI vision, sequence the right bets, and align teams to a measurable plan.'
  },
  {
    icon: ArchitectureIcon,
    title: 'AI Platform Architecture',
    desc: 'Design scalable, governed AI platforms that integrate cleanly with your existing stack.'
  },
  {
    icon: AgentIcon,
    title: 'AI Agent Development',
    desc: 'Build production-ready agents that act on enterprise data, tools, and workflows.'
  },
  {
    icon: RagIcon,
    title: 'RAG & Knowledge Systems',
    desc: 'Implement retrieval and knowledge systems that ground AI in your real, current information.'
  },
  {
    icon: WorkflowIcon,
    title: 'Workflow Automation',
    desc: 'Automate complex business workflows end-to-end with AI in the loop where it adds value.'
  },
  {
    icon: GovernanceIcon,
    title: 'AI Governance & Evaluation',
    desc: 'Stand up evaluation, observability, and governance practices that scale with deployment.'
  },
  {
    icon: PromptIcon,
    title: 'Prompt & LLM Optimization',
    desc: 'Tune prompts, models, and inference to improve quality, latency, and cost in production.'
  },
  {
    icon: CoachIcon,
    title: 'Executive & Team AI Coaching',
    desc: 'Coach leaders and practitioners to make sound AI decisions and ship with confidence.'
  }
];

const fadeUp = {
  hidden: { opacity: 0, y: 28 },
  visible: (i = 0) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.6, delay: i * 0.08, ease: [0.25, 0.46, 0.45, 0.94] }
  })
};

/* ─────────────────────────────────────────────────────────────
   Page
   ───────────────────────────────────────────────────────────── */
function ConsultingPage() {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <div className="cnsl">
      {/* ── Navigation ── */}
      <nav className={`cnsl-nav ${scrolled ? 'cnsl-nav--scrolled' : ''}`}>
        <div className="cnsl-nav__inner">
          <button className="cnsl-nav__burger" aria-label="Menu" onClick={() => setMenuOpen(o => !o)}>
            <span /><span /><span />
          </button>
          <a href="/" className="cnsl-nav__logo" aria-label="CerebralMachines home">
            <img src="/CMLogo.png" alt="CerebralMachines" />
          </a>
          <div className="cnsl-nav__links">
            <a href="/">Home</a>
            <a href="/platform">Platform</a>
            <a href="/how-it-works">How it Works</a>
            <a href="/use-cases">Use Cases</a>
            <a href="/consulting" className="cnsl-nav__link--active">Consulting</a>
          </div>
          <div className="cnsl-nav__actions">
            <button className="cnsl-btn--ghost" onClick={() => navigate('/login')}>Sign In</button>
            <button className="cnsl-btn--primary" onClick={() => navigate('/request-demo')}>Request a Demo</button>
          </div>
        </div>
        {menuOpen && (
          <div className="cnsl-nav__mobile-menu">
            <a href="/" onClick={(e) => { e.preventDefault(); setMenuOpen(false); navigate('/'); }}>Home</a>
            <a href="/platform" onClick={(e) => { e.preventDefault(); setMenuOpen(false); navigate('/platform'); }}>Platform</a>
            <a href="/how-it-works" onClick={(e) => { e.preventDefault(); setMenuOpen(false); navigate('/how-it-works'); }}>How it Works</a>
            <a href="/use-cases" onClick={(e) => { e.preventDefault(); setMenuOpen(false); navigate('/use-cases'); }}>Use Cases</a>
            <a href="/consulting" onClick={(e) => { e.preventDefault(); setMenuOpen(false); navigate('/consulting'); }}>Consulting</a>
            <a href="/login" onClick={(e) => { e.preventDefault(); setMenuOpen(false); navigate('/login'); }}>Sign In</a>
            <a href="/request-demo" onClick={(e) => { e.preventDefault(); setMenuOpen(false); navigate('/request-demo'); }}>Request a Demo</a>
          </div>
        )}
      </nav>

      {/* ══════════════════════════════════════════════
          SECTION 1 — HERO
          ══════════════════════════════════════════════ */}
      <section className="cnsl-hero">
        <div className="cnsl-hero__bg-glow" />
        <div className="cnsl-container">
          <motion.div
            className="cnsl-hero__content"
            initial="hidden"
            animate="visible"
            variants={fadeUp}
          >
            <span className="cnsl-hero__eyebrow">AI Consulting Services</span>
            <h1 className="cnsl-hero__title">
              Expert AI Teams for Companies Ready to Build Practical AI
            </h1>
            <p className="cnsl-hero__lead">
              CerebralMachines provides AI consulting, implementation, advisory, and coaching
              services to help companies design and build AI platforms, agents, and workflow
              automation solutions.
            </p>
            <p className="cnsl-hero__body">
              We help your team identify the right AI opportunities, select the right architecture,
              avoid costly mistakes, and move from AI ideas to working business systems.
            </p>
            <div className="cnsl-hero__cta">
              <button className="cnsl-btn--hero-primary" onClick={() => navigate('/request-demo')}>
                Schedule an Assessment
              </button>
              <button className="cnsl-btn--hero-secondary" onClick={() => navigate('/contact')}>
                Contact Us
              </button>
            </div>
          </motion.div>
        </div>
      </section>

      {/* ══════════════════════════════════════════════
          SECTION 2 — ASSESSMENT INTRO
          ══════════════════════════════════════════════ */}
      <section className="cnsl-intro">
        <div className="cnsl-container">
          <motion.div
            className="cnsl-intro__inner"
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: '-80px' }}
            variants={fadeUp}
          >
            <span className="cnsl-eyebrow">FIRST ENGAGEMENT</span>
            <h2 className="cnsl-intro__title">
              Start With an AI Opportunity &amp; Execution Assessment
            </h2>
            <div className="cnsl-intro__divider" />
            <p className="cnsl-intro__body">
              A focused first engagement to identify your highest-value AI opportunities and create
              a practical execution plan.
            </p>
          </motion.div>
        </div>
      </section>

      {/* ══════════════════════════════════════════════
          SECTION 3 — TWO GLASS CARDS
          ══════════════════════════════════════════════ */}
      <section className="cnsl-twoup">
        <div className="cnsl-container">
          <div className="cnsl-twoup__grid">
            <motion.article
              className="cnsl-card"
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, margin: '-80px' }}
              variants={fadeUp}
              custom={0}
            >
              <div className="cnsl-card__head">
                <div className="cnsl-card__icon">{EvaluateIcon}</div>
                <h3 className="cnsl-card__title">What We Help You Evaluate</h3>
              </div>
              <ul className="cnsl-card__list">
                {evaluateItems.map((item, i) => (
                  <li key={i}>
                    <span className="cnsl-card__check">{CheckIcon}</span>
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </motion.article>

            <motion.article
              className="cnsl-card"
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, margin: '-80px' }}
              variants={fadeUp}
              custom={1}
            >
              <div className="cnsl-card__head">
                <div className="cnsl-card__icon">{ReceiveIcon}</div>
                <h3 className="cnsl-card__title">What You Receive</h3>
              </div>
              <ul className="cnsl-card__list">
                {receiveItems.map((item, i) => (
                  <li key={i}>
                    <span className="cnsl-card__check">{CheckIcon}</span>
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </motion.article>
          </div>
        </div>
      </section>

      {/* ══════════════════════════════════════════════
          SECTION 4 — HOW WE HELP (light surface)
          ══════════════════════════════════════════════ */}
      <section className="cnsl-services">
        <div className="cnsl-container">
          <motion.div
            className="cnsl-section-header"
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: '-80px' }}
            variants={fadeUp}
          >
            <span className="cnsl-eyebrow cnsl-eyebrow--dark">SERVICES</span>
            <h2 className="cnsl-section-title cnsl-section-title--dark">
              How We Help After the Assessment
            </h2>
            <p className="cnsl-section-subtitle cnsl-section-subtitle--dark">
              From strategy to implementation, our teams partner with you across the full lifecycle
              of building, deploying, and operating production AI systems.
            </p>
          </motion.div>

          <div className="cnsl-services__grid">
            {services.map((s, i) => (
              <motion.article
                key={i}
                className="cnsl-service"
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, margin: '-60px' }}
                variants={fadeUp}
                custom={i}
              >
                <div className="cnsl-service__icon">{s.icon}</div>
                <h3 className="cnsl-service__title">{s.title}</h3>
                <p className="cnsl-service__desc">{s.desc}</p>
              </motion.article>
            ))}
          </div>
        </div>
      </section>

      {/* ══════════════════════════════════════════════
          SECTION 5 — FINAL CTA
          ══════════════════════════════════════════════ */}
      <section className="cnsl-cta">
        <div className="cnsl-cta__glow" />
        <div className="cnsl-container cnsl-container--narrow">
          <motion.div
            className="cnsl-cta__inner"
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: '-80px' }}
            variants={fadeUp}
          >
            <span className="cnsl-cta__badge">GET STARTED</span>
            <h2 className="cnsl-cta__title">
              Schedule an AI Opportunity &amp; Execution Assessment
            </h2>
            <p className="cnsl-cta__body">
              Find out where AI can create real value in your organization &mdash; and what it will
              take to build it.
            </p>
            <div className="cnsl-cta__buttons">
              <button className="cnsl-btn--hero-primary" onClick={() => navigate('/request-demo')}>
                Schedule an Assessment
              </button>
              <button className="cnsl-btn--hero-secondary" onClick={() => navigate('/contact')}>
                Talk to Our Team
              </button>
            </div>
            <p className="cnsl-cta__fine">
              Hands-on engagement &middot; Senior AI architects &middot; Outcome-driven delivery
            </p>
          </motion.div>
        </div>
      </section>

      {/* ── Footer ── */}
      <footer className="cnsl-footer">
        <div className="cnsl-footer__inner">
          <div className="cnsl-footer__brand">
            <a href="/" className="cnsl-footer__logo" aria-label="CerebralMachines home">
              <img src="/CMLogo.png" alt="CerebralMachines" />
            </a>
            <p className="cnsl-footer__tagline">
              The foundational intelligence platform for the enterprise.
            </p>
          </div>
          <div className="cnsl-footer__links">
            <div className="cnsl-footer__col">
              <h4>Product</h4>
              <ul>
                <li><a href="/platform">Platform</a></li>
                <li><a href="/how-it-works">How it Works</a></li>
                <li><a href="/use-cases">Use Cases</a></li>
                <li><a href="/consulting">Consulting</a></li>
              </ul>
            </div>
            <div className="cnsl-footer__col">
              <h4>Company</h4>
              <ul>
                <li><a href="/about">About</a></li>
                <li><a href="/contact">Contact</a></li>
                <li><a href="/careers">Careers</a></li>
              </ul>
            </div>
            <div className="cnsl-footer__col">
              <h4>Legal</h4>
              <ul>
                <li><a href="/privacy">Privacy Policy</a></li>
                <li><a href="/terms">Terms of Service</a></li>
              </ul>
            </div>
          </div>
        </div>
        <div className="cnsl-footer__bottom">
          <p>&copy; 2026 CerebralMachines. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
}

export default ConsultingPage;
